import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import AutoCompleteSelectField from "components/AutoCompleteSelectField";
import { ToggleButton } from "components";
import { ScoreTooltip } from "components/ScoreTooltip/ScoreTooltip";
import { SlideOver } from "components/SlideOver";
import { SkillsSelectField } from "./SkillsSelectField";
import { IIssueRatingCategory, ISelectOption } from "types";
import {
  AdjustmentsIcon,
  CollectionIcon,
  LightningBoltIcon,
  LocationMarkerIcon,
  TagIcon,
  UserIcon,
  XIcon,
} from "@heroicons/react/outline";

interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  onClearAll: () => void;
  onLocationChange: (value: any) => void;
  page: "issues" | "solutions" | "action-groups" | "handouts";
  keywordValue: string;
  onKeywordChange: (value: string) => void;
  categories?: IIssueRatingCategory[];
  selectedCategories: string[];
  onToggleCategory: (categoryId: string) => void;
  skillOptions: ISelectOption[];
  selectedSkills: ISelectOption[];
  onSkillsChange: (value: any) => void;
  onSkillSearch: (value: React.SetStateAction<string>) => void;
  showMyToggle: boolean;
  myToggleLabel: string;
  myToggleEnabled: boolean;
  onToggleMine: () => void;
  selectedLocationName?: string;
  hasLocationFilter?: boolean;
  onClearLocation?: () => void;
}

const sectionHeadingClasses = "text-sm font-semibold text-slate-800";
const sectionCardClasses =
  "rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_20px_45px_-30px_rgba(15,23,42,0.3)]";

const SectionBadge = ({
  icon,
  label,
}: {
  icon: JSX.Element;
  label: string;
}) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-600">
    {icon}
    <span>{label}</span>
  </span>
);

function FiltersDrawer({
  isOpen,
  onClose,
  onApply,
  onClearAll,
  onLocationChange,
  page,
  keywordValue,
  onKeywordChange,
  categories = [],
  selectedCategories,
  onToggleCategory,
  skillOptions,
  selectedSkills,
  onSkillsChange,
  onSkillSearch,
  showMyToggle,
  myToggleLabel,
  myToggleEnabled,
  onToggleMine,
  selectedLocationName,
  hasLocationFilter,
  onClearLocation,
}: FiltersDrawerProps) {
  const showCategories = page === "issues" && categories.length > 0;
  const showSkills =
    (page === "solutions" || page === "action-groups") &&
    skillOptions.length > 0;

  const activeFilters = useMemo(
    () => {
      const filters: {
        id: string;
        label: string;
        onRemove: () => void;
        icon: JSX.Element;
      }[] = [];

      if (hasLocationFilter && (selectedLocationName || onClearLocation)) {
        filters.push({
          id: "location",
          label: selectedLocationName || "Saved area",
          onRemove: () => {
            onLocationChange(null);
            onClearLocation?.();
          },
          icon: <LocationMarkerIcon className="h-4 w-4" />, 
        });
      }

      if (keywordValue) {
        filters.push({
          id: "keyword",
          label: `Tag: ${keywordValue}`,
          onRemove: () => onKeywordChange(""),
          icon: <TagIcon className="h-4 w-4" />,
        });
      }

      const categoryMap = new Map(
        categories.map((category) => [String(category.id), category.title])
      );
      selectedCategories.forEach((categoryId) => {
        const label = categoryMap.get(categoryId) || "Category";
        filters.push({
          id: `category-${categoryId}`,
          label,
          onRemove: () => onToggleCategory(categoryId),
          icon: <CollectionIcon className="h-4 w-4" />,
        });
      });

      selectedSkills.forEach((skill) => {
        filters.push({
          id: `skill-${skill.value}`,
          label: skill.label || "Skill",
          onRemove: () => {
            const remaining = selectedSkills.filter(
              (item) => item.value !== skill.value
            );
            onSkillsChange(remaining);
          },
          icon: <LightningBoltIcon className="h-4 w-4" />,
        });
      });

      if (showMyToggle && myToggleEnabled) {
        filters.push({
          id: "mine",
          label: myToggleLabel || "My items",
          onRemove: onToggleMine,
          icon: <UserIcon className="h-4 w-4" />,
        });
      }

      return filters;
    },
    [
      categories,
      hasLocationFilter,
      keywordValue,
      myToggleEnabled,
      myToggleLabel,
      onClearLocation,
      onKeywordChange,
      onSkillsChange,
      onToggleCategory,
      onToggleMine,
      selectedCategories,
      selectedLocationName,
      selectedSkills,
      showMyToggle,
      onLocationChange,
    ]
  );

  const hasActiveFilters = activeFilters.length > 0;

  const renderHeroHeader = useCallback(() => null, []);

  return (
    <SlideOver
      open={isOpen}
      close={onClose}
      overlayClassName="bg-black/30"
      classNames={{
        headerWrapper: "px-0",
        contentWrapper: "relative flex-1 flex flex-col font-normal text-slate-900",
        divider: "hidden",
      }}
      renderHeader={renderHeroHeader}
    >
      <div className="flex h-full flex-col gap-6 bg-slate-50/90 px-6 pb-6">
        <div className="relative flex flex-col gap-4 pt-6">
          <div className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-white" />
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-600">
              <AdjustmentsIcon className="h-3.5 w-3.5" />
              {page === "issues"
                ? "Issue Search"
                : page === "solutions"
                ? "Solution Search"
                : page === "action-groups"
                ? "Action Group Search"
                : "Search"}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
            >
              <span className="sr-only">Close search</span>
              <XIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="h-px w-full bg-slate-200" />

          <div className="mt-5 flex flex-col gap-5">
            <div
              className={classnames(sectionCardClasses, "flex flex-col gap-4")}
            >
              <SectionBadge
                icon={<LocationMarkerIcon className="h-3.5 w-3.5" />}
                label="Location & Tags"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <span className={sectionHeadingClasses}>Location</span>
                  <AutoCompleteSelectField
                    isSearchInput
                    showSearchIcon
                    filterOption={() => true}
                    onChange={onLocationChange}
                    placeholder="Type a city, state, or country"
                    className="w-full !pb-0"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className={sectionHeadingClasses}>Tags</span>
                  <input
                    value={keywordValue}
                    onChange={(event) => onKeywordChange(event.target.value)}
                    placeholder="Search tags"
                    className="h-11 w-full rounded-full border border-slate-200 px-4 text-sm text-slate-800 shadow-sm transition focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-300"
                  />
                </div>
              </div>
            </div>

            {showMyToggle && (
              <div
                className={classnames(
                  sectionCardClasses,
                  "flex flex-col gap-3"
                )}
              >
                <SectionBadge
                  icon={<UserIcon className="h-3.5 w-3.5" />}
                  label="Your Contributions"
                />
                <ToggleButton
                  className="w-full"
                  buttonClassName="w-full justify-center"
                  enabled={myToggleEnabled}
                  setEnabled={onToggleMine}
                  label={myToggleLabel}
                />
              </div>
            )}
          </div>
        </div>

        {showCategories && (
          <div
            className={classnames(sectionCardClasses, "flex flex-col gap-3")}
          >
            <SectionBadge
              icon={<CollectionIcon className="h-3.5 w-3.5" />}
              label="Categories"
            />
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const id = category.id as string;
                const isChecked = selectedCategories.includes(id);
                const tooltipId = `filters-category-${id}`;
                return (
                  <div key={id} className="relative">
                    <button
                      type="button"
                      data-tooltip-id={tooltipId}
                      className={classnames(
                        "rounded-full border px-4 py-2 text-sm font-semibold transition",
                        {
                          "bg-slate-900 text-white border-slate-900 shadow": isChecked,
                          "bg-white text-slate-700 border-slate-200 hover:border-slate-400": !isChecked,
                        }
                      )}
                      onClick={() => onToggleCategory(id)}
                    >
                      {category.title}
                    </button>
                    {category.description && (
                      <ScoreTooltip id={tooltipId} content={category.description} />
                    )}
                  </div>
                );
              })}
              {!categories.length && (
                <span className="text-sm text-slate-500">
                  No categories available.
                </span>
              )}
            </div>
          </div>
        )}

        {showSkills && (
          <SkillsSelectField
            currentSelectedSkills={selectedSkills}
            skillOptions={skillOptions}
            onSkillsChange={onSkillsChange}
            setSkillSearchParam={onSkillSearch}
            className="w-full"
            mainClassName={sectionCardClasses}
            badgeIcon={<LightningBoltIcon className="h-3.5 w-3.5" />}
            badgeLabel="Skills"
          />
        )}

        {hasActiveFilters && (
          <div className={classnames(sectionCardClasses)}>
            <div className="mb-3 flex items-center justify-between">
              <SectionBadge
                icon={<AdjustmentsIcon className="h-3.5 w-3.5" />}
                label="Active Search"
              />
              <button
                type="button"
                onClick={onClearAll}
                className="rounded-full bg-black/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:bg-black/10"
              >
                Reset All
              </button>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {activeFilters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={filter.onRemove}
                  className="group inline-flex items-center gap-2 rounded-full border border-slate-200/70 bg-white/80 px-3.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-white"
                >
                  <span className="flex items-center gap-1.5 text-slate-600 group-hover:text-slate-800">
                    {filter.icon}
                    <span className="text-sm font-semibold text-slate-700">
                      {filter.label}
                    </span>
                  </span>
                  <span className="rounded-full bg-slate-200/80 p-0.5 text-slate-600 group-hover:bg-slate-300">
                    <XIcon className="h-4 w-4" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </SlideOver>
  );
}

export default FiltersDrawer;
